"use client"

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import ServiceCard from "./ServiceCard";
import { Link } from "@/i18n/navigation";
import { 
  Hotel, 
  Utensils, 
  Calendar, 
  Music, 
  MapPin, 
  Camera, 
  Car, 
  Plane,
  Mountain,
  Waves
} from "lucide-react";
import hotelImage from "@/public/hotel-luxury.jpg";
import restaurantImage from "@/public/restaurant-gourmet.jpg";
import festivalImage from "@/public/festival-celebration.jpg";
import eventsImage from "@/public/events-concert.jpg";

const Services = () => {
  const t = useTranslations();

  const services = [
    {
      id: 1,
      title: t('services.luxuryHotels.title'),
      description: t('services.luxuryHotels.description'),
      image: hotelImage,
      link: "/hotels",
      size: "large" as const,
    },
    {
      id: 2,
      title: t('services.fineDining.title'),
      description: t('services.fineDining.description'),
      image: restaurantImage,
      link: "/restaurants",
      size: "medium" as const,
    },
    {
      id: 3,
      title: t('services.liveEvents.title'),
      description: t('services.liveEvents.description'),
      image: eventsImage,
      link: "/events",
      size: "medium" as const,
    },
    {
      id: 4,
      title: t('services.culturalFestivals.title'),
      description: t('services.culturalFestivals.description'),
      image: festivalImage,
      link: "/festivals",
      size: "medium" as const,
    },
  ];

  return (
    <section id="services" className="w-full py-12 sm:py-16 md:py-24 bg-background relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div 
          className="mb-8 sm:mb-12 md:mb-16 text-center sm:text-left"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
            <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-primary to-accent" />
            <span className="text-xs sm:text-sm font-medium tracking-widest uppercase text-primary">
              {t('services.whatWeOffer')}
            </span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-foreground mb-4 leading-tight">
            {t('services.discoverOur')}{" "}
            <span className="text-gradient">{t('common.services')}</span>
          </h2>
          
          <p className="text-muted-foreground text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl leading-relaxed mx-auto sm:mx-0">
            {t('services.description')}
          </p>
        </motion.div>

        {/* Mobile-First Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 auto-rows-auto">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              title={service.title}
              description={service.description}
              image={service.image}
              link={service.link}
              size={service.size}
              index={index}
            />
          ))}
        </div>
        
        {/* Bottom CTA */}
        <motion.div 
          className="mt-8 sm:mt-12 md:mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <a 
            href="/services" 
            className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-primary text-primary-foreground font-medium rounded-full hover:shadow-lg hover:shadow-primary/25 transition-all duration-300 hover:-translate-y-1 text-sm sm:text-base min-h-[44px]"
          >
            <span>{t('services.exploreAllServices')}</span>
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
